import { useEffect, useState } from 'react';
import { Camera, Loader2, Mic, Monitor, ScrollText, Volume2, Waves } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const MODE_CHOICES = [
  { id: 'teleprompter', label: 'Teleprompter', hint: 'Read a script that scrolls while you record', Icon: ScrollText },
  { id: 'freestyle', label: 'Independent audio', hint: 'Speak freely, no script', Icon: Waves },
];

const CAPTURE_CHOICES = [
  { id: 'audio', label: 'Audio only', Icon: Mic },
  { id: 'camera', label: 'Camera', Icon: Camera },
  { id: 'screen', label: 'Screen share', Icon: Monitor },
];

export function describeInsertPosition(position, segments) {
  const list = segments || [];
  const name = (seg, i) => `"${seg?.title || `Part ${i + 1}`}"`;
  if (!list.length || position >= list.length) {
    return list.length ? `Adds after ${name(list[list.length - 1], list.length - 1)}, at the end.` : 'Adds the first module.';
  }
  if (position <= 0) return `Adds before ${name(list[0], 0)}, at the beginning.`;
  return `Adds between ${name(list[position - 1], position - 1)} and ${name(list[position], position)}.`;
}

function ChoiceButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-1 items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
        active
          ? 'border-amber-400 bg-amber-400/10 text-amber-100'
          : 'border-slate-700 text-slate-300 hover:border-slate-500'
      }`}
    >
      {children}
    </button>
  );
}

export function PitchAddModuleDialog({
  open,
  onOpenChange,
  position = 0,
  segments = [],
  defaultCapture = 'audio',
  defaultMode = 'teleprompter',
  defaultSpeakerCamera = false,
  defaultSystemAudio = false,
  submitting = false,
  onSubmit,
}) {
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState(defaultMode);
  const [capture, setCapture] = useState(defaultCapture);
  const [speakerCamera, setSpeakerCamera] = useState(defaultSpeakerCamera);
  const [systemAudio, setSystemAudio] = useState(defaultSystemAudio);
  const [script, setScript] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setMode(defaultMode);
    setCapture(defaultCapture);
    setSpeakerCamera(defaultSpeakerCamera);
    setSystemAudio(defaultSystemAudio);
    setScript('');
  }, [open, defaultCapture, defaultMode, defaultSpeakerCamera, defaultSystemAudio]);

  const submit = (e) => {
    e?.preventDefault?.();
    const wantSpeaker = Boolean(speakerCamera);
    const wantSystem = Boolean(systemAudio);
    const nextCapture = (wantSpeaker || wantSystem) ? 'screen' : capture;
    onSubmit?.({
      position,
      title: title.trim(),
      recording_mode: mode,
      capture: nextCapture,
      speaker_camera: wantSpeaker && nextCapture === 'screen',
      system_audio: wantSystem && nextCapture === 'screen',
      teleprompter_text: mode === 'teleprompter' ? script : '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-slate-950 border-slate-700 text-slate-100">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Add a recording module</DialogTitle>
            <DialogDescription className="text-slate-400">
              {describeInsertPosition(position, segments)}
            </DialogDescription>
          </DialogHeader>

          <div>
            <Label htmlFor="pitch-module-title">Module title</Label>
            <Input
              id="pitch-module-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Part ${Math.min(position, segments.length) + 1}`}
              className="mt-1"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Recording type</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              {MODE_CHOICES.map(({ id, label, hint, Icon }) => (
                <ChoiceButton key={id} active={mode === id} onClick={() => setMode(id)} testId={`module-mode-${id}`}>
                  <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <span className="block font-medium">{label}</span>
                    <span className="block text-xs text-slate-500">{hint}</span>
                  </span>
                </ChoiceButton>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Capture</Label>
            <div className="flex gap-2">
              {CAPTURE_CHOICES.map(({ id, label, Icon }) => (
                <ChoiceButton key={id} active={capture === id} onClick={() => setCapture(id)} testId={`module-capture-${id}`}>
                  <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="font-medium">{label}</span>
                </ChoiceButton>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3">
              <div>
                <p className="text-sm font-medium">Speaker camera</p>
                <p className="text-xs text-slate-500">
                  Floats along the bottom of the shared screen (uses screen share for this module)
                </p>
              </div>
              <Switch
                checked={speakerCamera}
                onCheckedChange={(on) => {
                  setSpeakerCamera(on);
                  if (on) setCapture('screen');
                }}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5" /> System / tab audio
                </p>
                <p className="text-xs text-slate-500">
                  Mix shared tab sound with your mic (Chrome: Share tab audio)
                </p>
              </div>
              <Switch
                checked={systemAudio}
                onCheckedChange={(on) => {
                  setSystemAudio(on);
                  if (on) setCapture('screen');
                }}
              />
            </div>
          </div>

          {mode === 'teleprompter' ? (
            <div>
              <Label htmlFor="pitch-module-script">Teleprompter script</Label>
              <Textarea
                id="pitch-module-script"
                value={script}
                onChange={(e) => setScript(e.target.value)}
                rows={6}
                className="mt-1 text-sm"
                placeholder="What you want to read for this module… (you can edit it later)"
              />
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add module
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PitchAddModuleDialog;
